import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { customFetch } from "@workspace/api-client-react/custom-fetch";

export interface FolderItem {
  id: number;
  name: string;
  parentId: number | null;
  itemCount: number;
  createdAt: string;
}

export interface FolderAncestor {
  id: number;
  name: string;
}

export function useFolders(parentId: number | null) {
  const param = parentId == null ? "null" : String(parentId);
  return useQuery<FolderItem[]>({
    queryKey: ["folders", parentId],
    queryFn: () => customFetch<FolderItem[]>(`/api/folders?parentId=${param}`),
  });
}

export function useAllFolders() {
  return useQuery<FolderItem[]>({
    queryKey: ["all-folders"],
    queryFn: async () => {
      const root = await customFetch<FolderItem[]>(`/api/folders?parentId=null`);
      const all = [...root];
      async function loadChildren(folders: FolderItem[]) {
        for (const f of folders) {
          if (f.itemCount > 0) {
            const children = await customFetch<FolderItem[]>(`/api/folders?parentId=${f.id}`);
            all.push(...children);
            await loadChildren(children);
          }
        }
      }
      await loadChildren(root);
      return all;
    },
  });
}

export function useFolderAncestors(folderId: number | null) {
  return useQuery<FolderAncestor[]>({
    queryKey: ["folder-ancestors", folderId],
    queryFn: () =>
      folderId == null
        ? Promise.resolve([])
        : customFetch<FolderAncestor[]>(`/api/folders/${folderId}/ancestors`),
    enabled: folderId != null,
  });
}

export function useCreateFolder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; parentId: number | null }) =>
      customFetch<FolderItem>("/api/folders", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["folders", variables.parentId] });
      queryClient.invalidateQueries({ queryKey: ["all-folders"] });
    },
  });
}

export function useDeleteFolder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: number; parentId: number | null }) =>
      customFetch<{ success: boolean }>(`/api/folders/${id}`, { method: "DELETE" }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["folders", variables.parentId] });
      queryClient.invalidateQueries({ queryKey: ["folder-sessions"] });
      queryClient.invalidateQueries({ queryKey: ["all-folders"] });
    },
  });
}

export function useRenameFolder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: number; name: string; parentId: number | null }) =>
      customFetch<any>(`/api/folders/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ name }),
      }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["folders", variables.parentId] });
      queryClient.invalidateQueries({ queryKey: ["all-folders"] });
    },
  });
}

export function useFolderSessions(folderId: number | null) {
  const param = folderId == null ? "null" : String(folderId);
  return useQuery({
    queryKey: ["folder-sessions", folderId],
    queryFn: () => customFetch<any[]>(`/api/sessions?folderId=${param}`),
  });
}

export function useRenameSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, title }: { id: number; title: string }) =>
      customFetch<any>(`/api/sessions/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ title }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["folder-sessions"] });
      queryClient.invalidateQueries({ queryKey: ["listSessions"] });
    },
  });
}

export function useMoveSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, folderId }: { id: number; folderId: number | null }) =>
      customFetch<any>(`/api/sessions/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ folderId }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["folder-sessions"] });
      queryClient.invalidateQueries({ queryKey: ["folders"] });
      queryClient.invalidateQueries({ queryKey: ["all-folders"] });
      queryClient.invalidateQueries({ queryKey: ["listSessions"] });
    },
    onError: (err: any) => {
      console.error("[MoveSession] PATCH failed:", err?.data || err?.message || err);
    },
  });
}

export function useDeleteSessionFromDashboard() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: number }) =>
      customFetch<any>(`/api/sessions/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["folder-sessions"] });
      queryClient.invalidateQueries({ queryKey: ["folders"] });
      queryClient.invalidateQueries({ queryKey: ["all-folders"] });
      queryClient.invalidateQueries({ queryKey: ["listSessions"] });
      queryClient.invalidateQueries({ queryKey: ["getProfile"] });
    },
  });
}
